import { RouterProvider } from "react-router-dom";
import { router } from "./router";
import { useState, useEffect } from "react";
import { useSettingsStore } from "./stores/settings";
import "./lib/performance";
import { initializePushNotifications } from "./lib/push";

function App() {
  const theme = useSettingsStore((state) => state.appearance.theme);
  const fontSizeScale = useSettingsStore((state) => state.appearance.fontSizeScale);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: any) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Update UI notify the user they can install the PWA
      setShowInstallButton(true);
    };

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    const handleAppInstalled = () => {
      // Hide the app-provided install promotion
      setShowInstallButton(false);
      // Clear the deferredPrompt so it can be garbage collected
      setDeferredPrompt(null);
      // Optionally, send analytics event to indicate successful install
      console.log('PWA was installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('fullscreenchange', handleFullscreenChange);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('fullscreenchange', handleFullscreenChange);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to the install prompt: ${outcome}`);
    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  useEffect(() => {
    // Apply font size scale to the root element
    document.documentElement.style.fontSize = `${16 * fontSizeScale}px`;

    // Attempt to register for push notifications
    initializePushNotifications();
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;

    // Remove existing font size when theme changes
    root.style.fontSize = '';

    root.classList.remove("light", "dark");

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      // Apply font size scale with system theme
      root.style.fontSize = `${16 * fontSizeScale}px`;
      root.classList.add(systemTheme);

      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

      const handleChange = (e: MediaQueryListEvent) => {
        // Only apply system theme change if the setting is still 'system'
        if (useSettingsStore.getState().appearance.theme === "system") {
          root.classList.remove("light", "dark");
          root.classList.add(e.matches ? "dark" : "light");
        }
      };

      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    } else {
      // Apply font size scale with specific theme
      root.style.fontSize = `${16 * fontSizeScale}px`;
      root.classList.add(theme);
    }
  }, [theme]);

  useEffect(() => {
    // Update CSS variable when font size scale changes
    document.documentElement.style.setProperty('--font-scale', fontSizeScale.toString());
    
    // Apply font size scale when it changes
    document.documentElement.style.fontSize = `${16 * fontSizeScale}px`;
  }, [fontSizeScale]);

  return (
    <>
      {showInstallButton && !isFullscreen && (
        <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999 }}>
          <button onClick={handleInstallClick} style={{ padding: '10px 20px', background: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
            Install App
          </button>
        </div>
      )}
      <RouterProvider router={router} />
    </>
  );
}

export default App;